import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Trash2, Download, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const ManageArchives = () => {
  const navigate = useNavigate();
  const { token, getAuthHeaders } = useAuth();
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [archiveToDelete, setArchiveToDelete] = useState(null);

  useEffect(() => {
    fetchArchives();
  }, []);

  const fetchArchives = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching archives from:', `${API_BASE_URL}/calendars/archives`);
      const response = await axios.get(`${API_BASE_URL}/calendars/archives`, {
        headers: getAuthHeaders()
      });
      console.log('Archives response:', response.data);
      setArchives(response.data.data || []);
    } catch (err) {
      console.error('Error fetching archives:', err);
      console.error('Error response:', err.response);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load archives';
      setError(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (archive) => {
    setArchiveToDelete(archive);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!archiveToDelete) return;

    setShowDeleteModal(false);

    try {
      await axios.delete(`${API_BASE_URL}/calendars/archives/${archiveToDelete.id}`, {
        headers: getAuthHeaders()
      });
      
      // Show success notification
      const notification = document.createElement('div');
      notification.style.cssText = 'position:fixed;top:20px;right:20px;background:#10b981;color:white;padding:16px 24px;border-radius:8px;box-shadow:0 4px 6px rgba(0,0,0,0.1);z-index:10000;max-width:400px;';
      notification.innerHTML = `<div style="font-weight:bold;">✅ Archive Deleted</div>`;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
      
      fetchArchives();
    } catch (err) {
      console.error('Error deleting archive:', err);
      setError(err.response?.data?.detail || 'Failed to delete archive');
    }
  };

  const handleDownload = async (archiveId, archiveName) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/calendars/archives/${archiveId}`, {
        headers: getAuthHeaders()
      });
      
      // Download as JSON file
      const dataStr = JSON.stringify(response.data.data.export_data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${archiveName.replace(/\s+/g, '-')}.json`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      // Show success notification
      const notification = document.createElement('div');
      notification.style.cssText = 'position:fixed;top:20px;right:20px;background:#10b981;color:white;padding:16px 24px;border-radius:8px;box-shadow:0 4px 6px rgba(0,0,0,0.1);z-index:10000;max-width:400px;';
      notification.innerHTML = `
        <div style="font-weight:bold;">✅ Downloaded!</div>
        <div style="font-size:14px;margin-top:4px;">Check your downloads folder</div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    } catch (err) {
      console.error('Error downloading archive:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/calendars')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Calendars
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Manage Archives</h1>
          <p className="text-gray-600 mt-2">View and manage exported calendar archives</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Archives Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
            <p className="mt-4 text-gray-600">Loading archives...</p>
          </div>
        ) : archives.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Archives Yet</h3>
            <p className="text-gray-600">
              Export a calendar to create your first archive
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Archive Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Original Calendar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Services
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Servers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Appointments
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {archives.map((archive) => (
                  <tr key={archive.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        {archive.name}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">
                        {archive.original_calendar_name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {new Date(archive.created_at).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {archive.counts?.services || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {archive.counts?.servers || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {archive.counts?.appointments || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDownload(archive.id, archive.name)}
                        className="text-green-600 hover:text-green-900 mr-3"
                        title="Download JSON"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(archive)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Archive"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && archiveToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="ml-4 text-xl font-bold text-gray-900">Delete Archive</h3>
              </div>
              
              <p className="text-gray-600 mb-2">
                Are you sure you want to delete this archive?
              </p>
              
              <div className="bg-gray-50 rounded-md p-3 mb-4">
                <p className="font-semibold text-gray-900">{archiveToDelete.name}</p>
                <p className="text-sm text-gray-600 mt-1">
                  Original: {archiveToDelete.original_calendar_name}
                </p>
                <p className="text-sm text-gray-600">
                  Created: {new Date(archiveToDelete.created_at).toLocaleString()}
                </p>
              </div>
              
              <p className="text-sm text-red-600 font-medium mb-6">
                ⚠️ This action cannot be undone.
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md"
                >
                  Delete Archive
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageArchives;
